<template>
  <v-form @submit.prevent="onSubmit" v-if="isReady">
    <div v-if="isEmpty(customerAssessmentTerms) && this.customer.parent_id != null">
      <parent-assessment-terms :customer_id="this.customer.parent_id"/>
    </div>
    <div v-else>
      <div id="causes" class="my-4">
        <div class="d-flex justify-space-between">
          <h3>
            {{ $tc('cause', 2) }}
          </h3>
          <v-btn color="primary" outlined size="sm" @click="addTerm('cause')">
            <v-icon>mdi-plus</v-icon>
            {{$t('customer_assessment_terms_ui.new_cause')}}
          </v-btn>
        </div>
        <div v-for="(term, index) in causeTerms" :key="index">
          <div class="d-flex align-center">
            <v-text-field
              v-model="term.value"
              :disabled="(Boolean(term.locked) || term.isArchived)"
              :placeholder="$t('customer_assessment_terms_ui.cause_of_violation')"
              :prepend-inner-icon="term.locked ? 'mdi-lock' : null"
              :error-messages="get(causeErrors, index, [])"
              @input="setDirty(term)"
              dense
            />
            <v-checkbox
              v-model="term.isArchived"
              :label="$t('archived')"
              @change="setDirty(term)"
              dense/>
          </div>
        </div>
      </div>
      <div id="actions" class="my-4">
        <div class="d-flex justify-space-between">
          <h3>
            {{ $tc('action', 2) }}
          </h3>
          <v-btn color="primary" outlined size="sm" @click="addTerm('action')">
            <v-icon>mdi-plus</v-icon>
            {{$t('customer_assessment_terms_ui.new_action')}}
          </v-btn>
        </div>
        <div v-for="(term, index) in actionTerms" :key="index">
          <div class="d-flex align-center">
            <v-text-field
              v-model="term.value"
              :disabled="(Boolean(term.locked) || term.isArchived)"
              :placeholder="$t('customer_assessment_terms_ui.cause_of_violation')"
              :prepend-inner-icon="term.locked ? 'mdi-lock' : null"
              :error-messages="get(actionErrors, index, [])"
              @input="setDirty(term)"
              dense
            />
            <v-checkbox
              v-model="term.isArchived"
              :label="$t('archived')"
              @change="setDirty(term)"
              dense/>
          </div>
        </div>
      </div>
      <v-btn type="submit"
         color="primary"
         :loading="isSaving" block >
        <v-icon class="ml-n2">mdi-content-save</v-icon>
        {{ $t('save') }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import Vue from 'vue'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import ParentAssessmentTerms from '@/components/customer/ParentAssessmentTerms'
import { required } from 'vuelidate/lib/validators'
import { map, filter, isEmpty, each, get } from 'lodash'

export default {
  name: 'CustomerAssessmentTerms',
  components: {
    ParentAssessmentTerms
  },
  mixins: [
    ApiUtilitiesMixin
  ],
  props: {
    customer: Object,
    defaultTerms: Array,
    category: String
  },
  validations: {
    causeTerms: {
      $each: {
        value: {
          required
        }
      }
    },
    actionTerms: {
      $each: {
        value: {
          required
        }
      }
    }
  },
  data () {
    return {
      isEmpty: isEmpty,
      isReady: false,
      isSaving: false,
      customerAssessmentTerms: []
    }
  },
  methods: {
    get: get,
    /**
     * Retrieve the CustomerAssessmentTerm models for this customer.
     * @returns {Promise<T>}
     */
    fetchCustomerAssessmentTerms () {
      return this.$orm('CustomerAssessmentTerm').findAll({
        customer_id: this.customer.id,
        category: this.category
      })
        .then(response => {
          // If no terms, and this is a root customer, then provide default terms.
          if (isEmpty(response.getItems()) && this.customer.parent_id == null) {
            this.customerAssessmentTerms = map(this.defaultTerms, term => {
              Vue.set(term, 'dirty', true)
              return this.$orm('CustomerAssessmentTerm').createRecord(term)
            })
          } else {
            this.customerAssessmentTerms = response.getItems()
          }
        })
        .catch((error) => {
          if (error) {
            this.renderHttpAdapterErrorBanner(error)
            throw (error)
          }
        })
    },
    /**
     * Add a new term to the list in the form.
     * @param type
     */
    addTerm (type) {
      const newTerm = this.$orm('CustomerAssessmentTerm').createRecord({
        customer_id: this.customer.id,
        category: this.category,
        type: type,
        value: '',
        archived: null,
        locked: 0
      })
      this.customerAssessmentTerms.push(newTerm)
    },
    /**
     * Set the specified term to dirty, so we can keep track of the changed items in the form.
     * @param term
     */
    setDirty (term) {
      Vue.set(term, 'dirty', true)
    },
    /**
     * Submit the form and save the updated terms. Save only the new and modified terms.
     * @returns {boolean}
     */
    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }

      this.isSaving = true
      const dirtyTerms = filter(this.customerAssessmentTerms, x => x.dirty)
      const promises = []

      each(dirtyTerms, (term) => {
        promises.push(this.$orm('CustomerAssessmentTerm').createOrUpdate(term))
      })

      Promise.all(promises)
        .then((promises) => {
          this.$emit('saved', promises)
        })
        .catch((error) => {
          if (error) {
            this.renderErrorFlashBanner(error)
            throw (error)
          }
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  },
  computed: {
    /** Returns all terms with the 'cause' type */
    causeTerms () {
      return filter(this.customerAssessmentTerms, x => x.type === 'cause')
    },
    /** Returns all terms with the 'action' type */
    actionTerms () {
      return filter(this.customerAssessmentTerms, x => x.type === 'action')
    },
    causeErrors () {
      return map(this.$v.causeTerms.$each.$iter, validation => {
        if (get(validation, '$error') === true) {
          return this.$t('required')
        }
        return null
      })
    },
    actionErrors () {
      return map(this.$v.actionTerms.$each.$iter, validation => {
        if (get(validation, '$error') === true) {
          return this.$t('required')
        }
        return null
      })
    }
  },
  mounted () {
    this.fetchCustomerAssessmentTerms()
      .finally(() => {
        this.isReady = true
      })
  }
}
</script>

<style scoped>
  .small-icon {
    font-size: 1.5rem;
  }
</style>
